import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  DashboardChart
} from '@tonic/shared/components/ui';
import { Plus, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const mockMetrics = [
  {
    label: "Active Repairs",
    value: "245",
    trend: "+8%",
    description: "vs. last month",
  },
  {
    label: "Completion Rate",
    value: "92%",
    trend: "+3%",
    description: "vs. last month",
  },
  {
    label: "Avg Repair Time",
    value: "3.5 days",
    trend: "-12%",
    description: "vs. last month",
  },
  {
    label: "Customer Satisfaction",
    value: "4.7/5",
    trend: "+4%",
    description: "vs. last month",
  },
];

const mockRecentRepairs = [
  {
    id: "REP001",
    customer: "John Doe",
    device: "iPhone 13",
    issue: "Screen Replacement",
    status: "In Progress",
    technician: "Mike Wilson",
    startDate: "2024-01-15",
  },
  {
    id: "REP002",
    customer: "Jane Smith",
    device: "MacBook Pro",
    issue: "Battery Replacement",
    status: "Completed",
    technician: "Sarah Johnson",
    startDate: "2024-01-14",
  },
];

// Helper function to get status badge variant
function getStatusBadgeVariant(status) {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'default';
    case 'in progress':
      return 'secondary';
    case 'pending':
      return 'outline';
    default:
      return 'secondary';
  }
}

// Mock data for repair types
const repairTypeData = [
  { type: 'Hardware', count: 45 },
  { type: 'Software', count: 32 },
  { type: 'Battery', count: 28 },
  { type: 'Screen', count: 24 },
  { type: 'Other', count: 16 },
];

// Mock data for repair volume
const repairVolumeData = [
  { date: '2024-01-14', volume: 12 },
  { date: '2024-01-15', volume: 15 },
  { date: '2024-01-16', volume: 18 },
  { date: '2024-01-17', volume: 14 },
  { date: '2024-01-18', volume: 16 },
  { date: '2024-01-19', volume: 19 },
  { date: '2024-01-20', volume: 21 },
];

export function RepairOverviewPage() {
  const navigate = useNavigate();

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Repair Overview</h1>
          <p className="text-sm text-gray-500">Monitor repair operations and performance</p>
        </div>
        <Button onClick={() => navigate('/admin/re-commerce/repairs/list')}>
          <Plus className="w-4 h-4 mr-2" />
          New Repair
        </Button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {mockMetrics.map((metric) => (
          <Card key={metric.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{metric.label}</CardTitle>
              <Badge variant={metric.trend.startsWith('+') ? 'default' : 'destructive'}>
                {metric.trend.startsWith('+') ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {metric.trend}
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metric.value}</div>
              <p className="text-xs text-neutral-500">{metric.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Repair Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              title="Daily Repair Volume"
              data={repairVolumeData}
              dataKey="volume"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Repair Types</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="bar"
              title="Distribution by Type"
              data={repairTypeData}
              dataKey="count"
              xAxisKey="type"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>
      </div>

      {/* Recent Repairs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Repairs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-neutral-200">
            {mockRecentRepairs.map((repair) => (
              <div
                key={repair.id}
                className="py-4 flex items-center justify-between hover:bg-neutral-50 cursor-pointer"
                onClick={() => navigate(`/admin/re-commerce/repairs/${repair.id}`)}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{repair.device}</span>
                    <Badge variant={getStatusBadgeVariant(repair.status)}>
                      {repair.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-neutral-500">
                    {repair.customer} • {repair.issue}
                  </div>
                </div>
                <div className="text-sm text-neutral-500">
                  {repair.technician} • {repair.startDate}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
